import React, { useRef, useEffect } from "react";
import { Card } from "../ui/card";
import { Badge } from "../ui/badge";
import { AlertCircle } from "lucide-react";
import { cn } from "../../lib/utils";
import MarkdownMessage from "./MarkdownMessage";

/**
 * ChatMessages Component
 * Displays the chat messages with auto-scroll functionality
 */
const ChatMessages = ({
  messages,
  isTyping,
  onSuggestionClick,
  isSmallMobile = false,
  scrollToBottom = true,
}) => {
  const messagesEndRef = useRef(null);

  const scrollToEnd = (smooth = true) => {
    messagesEndRef.current?.scrollIntoView({
      behavior: smooth ? "smooth" : "auto",
      block: "end",
    });
  };

  // Scroll to bottom when messages change
  useEffect(() => {
    if (scrollToBottom) {
      scrollToEnd();
    }
  }, [messages, scrollToBottom]);

  // Expose scroll function via ref
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollToEnd = scrollToEnd;
    }
  }, []);

  return (
    <div
      className="flex-1 overflow-auto p-2 sm:p-3 bg-gradient-to-b from-muted/30 to-muted/60"
      style={{ WebkitOverflowScrolling: "touch" }}
    >
      {messages.map((message, index) => (
        <div
          key={index}
          className={cn(
            "flex mb-2 sm:mb-3 px-1 sm:px-0 animate-in fade-in slide-in-from-bottom-2 duration-300",
            message.type === "user" ? "justify-end" : "justify-start"
          )}
        >
          <Card
            className={cn(
              "p-3 sm:p-4 max-w-[85%] sm:max-w-[80%] break-words shadow-sm transition-all hover:shadow-md",
              message.type === "user"
                ? "bg-gradient-to-br from-primary to-primary/90 text-white rounded-2xl rounded-br-sm"
                : message.isError
                ? "bg-red-50 border-red-200 rounded-2xl rounded-bl-sm"
                : "bg-background rounded-2xl rounded-bl-sm"
            )}
          >
            {message.isError && (
              <div className="flex items-center gap-2 mb-2 text-red-600">
                <AlertCircle className="w-4 h-4" />
                <span className="text-xs font-semibold">Error</span>
              </div>
            )}
            <MarkdownMessage
              content={message.content}
              isUserMessage={message.type === "user"}
              className={cn(
                "text-xs sm:text-sm",
                message.isError && "text-red-800"
              )}
            />
            {message.suggestions && message.suggestions.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-1 sm:gap-2">
                {message.suggestions
                  .slice(0, isSmallMobile ? 2 : 3)
                  .map((suggestion, i) => (
                    <Badge
                      key={i}
                      className="text-[0.7rem] sm:text-xs h-5 sm:h-6 cursor-pointer bg-primary/10 text-primary border-primary/30 hover:bg-primary/20 hover:border-primary/50 hover:scale-105 active:scale-95 transition-all font-medium"
                      onClick={() => onSuggestionClick(suggestion)}
                    >
                      {suggestion}
                    </Badge>
                  ))}
              </div>
            )}
          </Card>
        </div>
      ))}

      {isTyping && (
        <div className="flex justify-start mb-3 animate-in fade-in slide-in-from-bottom-2">
          <Card className="p-4 bg-background rounded-2xl rounded-bl-sm shadow-sm">
            <div className="flex items-center gap-1.5">
              <div className="w-2 h-2 rounded-full bg-primary animate-bounce" />
              <div
                className="w-2 h-2 rounded-full bg-primary animate-bounce"
                style={{ animationDelay: "0.2s" }}
              />
              <div
                className="w-2 h-2 rounded-full bg-primary animate-bounce"
                style={{ animationDelay: "0.4s" }}
              />
            </div>
          </Card>
        </div>
      )}
      <div ref={messagesEndRef} />
    </div>
  );
};

export default ChatMessages;
